import { useEffect, useRef, useState } from "react";
import { X, TerminalSquare, Loader2, History, RotateCcw, RefreshCw, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VENDORS = [
  { id: "zte", label: "ZTE (GPON)" },
  { id: "cdata", label: "C-Data (GPON)" },
  { id: "hioso", label: "HiOSO (EPON)" },
  { id: "vsol", label: "V-SOL (EPON)" },
  { id: "custom", label: "Własne komendy" },
];

const VENDOR_MAP = { zte: "zte", vsol: "vsol", "v-sol": "vsol", cdata: "cdata", "c-data": "cdata", hioso: "hioso" };

const inp = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
const Field = ({ label, span, children }) => (
  <div className={span ? "col-span-2" : ""}>
    <label className="block text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</label>
    {children}
  </div>
);

export function OltProvision({ defaultPort = "", initial = null, onClose }) {
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [f, setF] = useState({ device_id: "", proto: "telnet", vendor: "zte", port: initial?.port ? String(initial.port) : defaultPort, onu: initial?.onu ? String(initial.onu) : "", vlan: initial?.vlan ? String(initial.vlan) : "", ppp_user: initial?.ppp_user || "", ppp_pass: "", username: "", password: "", enable: "", cli_port: "", commands: "", bulk: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRes, setBulkRes] = useState(null);
  const [logs, setLogs] = useState(null);
  const [logsBusy, setLogsBusy] = useState(false);
  useEffect(() => {
    setLoadingDevices(true);
    api.get("/snmp/devices").then((r) => {
      const list = r.data || [];
      setDevices(list);
      if (initial?.olt_name && list.length) {
        const on = String(initial.olt_name).toLowerCase();
        const m = list.find((d) => String(d.name || "").toLowerCase().includes(on) || on.includes(String(d.name || "").toLowerCase()));
        if (m) setF((s) => (s.device_id ? s : { ...s, device_id: m.id, vendor: VENDOR_MAP[String(m.manufacturer || "").toLowerCase()] || s.vendor }));
      }
    }).catch(() => {}).finally(() => setLoadingDevices(false));
  }, [initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onPickDevice = (id) => {
    const d = devices.find((x) => x.id === id);
    const man = String(d?.manufacturer || "").toLowerCase();
    setF((s) => ({ ...s, device_id: id, vendor: VENDOR_MAP[man] || s.vendor }));
  };
  const applyLog = (l) => {
    setF((s) => ({ ...s, device_id: l.device_id || s.device_id, vendor: l.vendor || s.vendor, proto: l.proto || s.proto,
      port: l.port ? String(l.port) : "", onu: l.onu ? String(l.onu) : "", vlan: l.vlan ? String(l.vlan) : "", ppp_user: l.ppp_user || "" }));
    setRes(null);
    toast.info("Wczytano konfigurację z historii — uzupełnij hasło PPPoE i wykonaj ponownie");
  };

  const run = async () => {
    if (!f.device_id) { toast.error("Wybierz urządzenie OLT"); return; }
    setBusy(true); setRes(null); setBulkRes(null);
    try {
      const base = { device_id: f.device_id, proto: f.proto, vendor: f.vendor, port: f.port, vlan: f.vlan,
        ppp_user: f.ppp_user, ppp_pass: f.ppp_pass, username: f.username, password: f.password,
        enable: f.enable, cli_port: f.cli_port ? Number(f.cli_port) : undefined, verify: true, client_id: initial?.client_id };
      if (bulkMode) {
        const items = f.bulk.split("\n").map((x) => x.trim()).filter(Boolean).map((line) => {
          const [onu, vlan, pu, pp] = line.split(",").map((s) => (s || "").trim());
          return { onu, vlan: vlan || undefined, ppp_user: pu || undefined, ppp_pass: pp || undefined, client_id: initial?.client_id };
        });
        if (!items.length) { toast.error("Podaj listę ONU (jedna na linię)"); setBusy(false); return; }
        const { data } = await api.post("/snmp/onu-provision-bulk", { ...base, items });
        setBulkRes(data);
        toast[data.ok_count === data.count ? "success" : "error"](`Skonfigurowano ${data.ok_count}/${data.count} ONU`);
      } else {
        const body = { ...base, onu: f.onu };
        if (f.vendor === "custom") body.commands = f.commands.split("\n").map((x) => x.trim()).filter(Boolean);
        const { data } = await api.post("/snmp/onu-provision", body);
        setRes(data);
        if (data.verify?.found && data.verify.rx_dbm != null && initial?.client_id) toast.success("RX zapisany do karty klienta");
        toast[data.ok ? "success" : "error"](data.ok ? "Provisioning wykonany" : "Nie udało się połączyć z OLT");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Błąd provisioningu"); }
    finally { setBusy(false); }
  };

  const loadLogs = async () => {
    setLogsBusy(true);
    try {
      const qs = f.device_id ? `?device_id=${f.device_id}&limit=15` : "?limit=15";
      const { data } = await api.get(`/snmp/provision-logs${qs}`);
      setLogs(data || []);
    } catch (err) { toast.error("Nie udało się pobrać historii"); setLogs([]); }
    finally { setLogsBusy(false); }
  };

  const liveRef = useRef(null);
  const notifiedRef = useRef(false);
  const [live, setLive] = useState(null);
  const [liveOn, setLiveOn] = useState(false);
  const [notify, setNotify] = useState(localStorage.getItem("fp_notify_onu") !== "0");
  const stopLive = () => { if (liveRef.current) { clearInterval(liveRef.current); liveRef.current = null; } setLiveOn(false); };
  const startLive = () => {
    if (!f.device_id || !f.onu) { toast.error("Wybierz urządzenie i podaj numer ONU"); return; }
    stopLive(); setLive(null); setLiveOn(true); notifiedRef.current = false;
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await api.get(`/snmp/onu-status?device_id=${f.device_id}&port=${encodeURIComponent(f.port)}&onu=${encodeURIComponent(f.onu)}`);
        setLive(data);
        if (data?.found) {
          if (notify && !notifiedRef.current) {
            notifiedRef.current = true;
            api.post("/snmp/notify-onu-registered", { device_id: f.device_id, port: f.port, onu: f.onu, client_id: initial?.client_id })
              .then((r) => toast.success(r.data?.push_sent ? `ONU zarejestrowana — powiadomiono montera (${r.data.push_sent})` : "ONU zarejestrowana"))
              .catch(() => {});
          }
          stopLive();
          if (initial?.client_id && data.rx_dbm != null) {
            api.post("/snmp/save-onu-rx", { client_id: initial.client_id, rx_dbm: data.rx_dbm })
              .then(() => toast.success("RX zapisany do karty klienta")).catch(() => {});
          }
          return;
        }
      } catch (e) { /* retry next tick */ }
      if (Date.now() - started > 30000) stopLive();
    };
    tick();
    liveRef.current = setInterval(tick, 3000);
  };
  const exportHistory = async (fmt) => {
    try {
      const qs = f.device_id ? `?device_id=${f.device_id}` : "";
      const { data } = await api.get(`/snmp/provision-logs/export.${fmt}${qs}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = URL.createObjectURL(new Blob([data], { type }));
      const a = document.createElement("a"); a.href = url; a.download = `provisioning_history.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Eksport nieudany"); }
  };
  const handleCsv = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let lines = String(reader.result).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length && !/^\d/.test(lines[0].split(/[,;\t]/)[0].trim())) lines.shift();
      const norm = lines.map((l) => l.split(/[;\t]/).join(",")).join("\n");
      setF((s) => ({ ...s, bulk: norm }));
      toast.success(`Wczytano ${lines.length} pozycji z CSV`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };
  const downloadTemplate = () => {
    const csv = "onu,vlan,ppp_user,ppp_pass\n1,100,jan,haslo\n2,100\n3,101,anna,haslo2\n";
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "wzor_onu.csv"; a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => () => stopLive(), []);

  return (
    <div className="fixed inset-0 z-[950] bg-black/60 flex items-center justify-center p-4" data-testid="olt-provision-modal">
      <div className="bg-slate-950 border border-slate-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-cyan-300 flex items-center gap-2"><TerminalSquare size={18} /> Provisioning ONU (CLI)</h3>
          <button onClick={onClose} data-testid="olt-provision-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Urządzenie OLT (SNMP/CLI)" span>
            <select value={f.device_id} onChange={(e) => onPickDevice(e.target.value)} data-testid="prov-device" className={inp}>
              <option value="">Wybierz urządzenie…</option>
              {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {loadingDevices
              ? <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Ładowanie urządzeń…</div>
              : devices.length === 0 && <div className="text-[10px] text-amber-400 mt-0.5">Brak urządzeń — dodaj OLT w module Monitoring (SNMP/CLI).</div>}
          </Field>
          <Field label="Protokół">
            <select value={f.proto} onChange={(e) => set("proto", e.target.value)} data-testid="prov-proto" className={inp}>
              <option value="telnet">Telnet</option><option value="ssh">SSH</option>
            </select>
          </Field>
          <Field label="Producent (szablon)">
            <select value={f.vendor} onChange={(e) => set("vendor", e.target.value)} data-testid="prov-vendor" className={inp}>
              {VENDORS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Port PON"><input value={f.port} onChange={(e) => set("port", e.target.value)} data-testid="prov-port" placeholder="np. 1" className={inp} /></Field>
          <Field label="ONU index"><input value={f.onu} onChange={(e) => set("onu", e.target.value)} data-testid="prov-onu" placeholder="np. 1" className={inp} /></Field>
          <Field label="VLAN"><input value={f.vlan} onChange={(e) => set("vlan", e.target.value)} data-testid="prov-vlan" placeholder="1–4094" className={inp} /></Field>
          <Field label="Port CLI (opcj.)"><input value={f.cli_port} onChange={(e) => set("cli_port", e.target.value)} data-testid="prov-cliport" placeholder="np. 22 / 23" className={inp} /></Field>
          <Field label="PPPoE login"><input value={f.ppp_user} onChange={(e) => set("ppp_user", e.target.value)} data-testid="prov-pppuser" placeholder="login" className={inp} /></Field>
          <Field label="PPPoE hasło"><input type="password" value={f.ppp_pass} onChange={(e) => set("ppp_pass", e.target.value)} data-testid="prov-ppppass" placeholder="hasło" className={inp} /></Field>
          <Field label="Login OLT (nadpisz)"><input value={f.username} onChange={(e) => set("username", e.target.value)} data-testid="prov-user" placeholder="opcjonalnie" className={inp} /></Field>
          <Field label="Hasło OLT (nadpisz)"><input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="prov-pass" placeholder="opcjonalnie" className={inp} /></Field>
          <Field label="Komenda enable (opcj.)" span><input value={f.enable} onChange={(e) => set("enable", e.target.value)} data-testid="prov-enable" placeholder="np. enable" className={inp} /></Field>
          {f.vendor === "custom" && (
            <Field label="Własne komendy CLI (jedna na linię)" span>
              <textarea value={f.commands} onChange={(e) => set("commands", e.target.value)} data-testid="prov-commands" rows={5}
                placeholder="Placeholdery: {port} {onu} {vlan} {ppp_user} {ppp_pass}" className={`${inp} font-mono text-xs`} />
            </Field>
          )}
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer" title="Konfiguruje wiele ONU naraz. Puste pola w linii dziedziczą z formularza (VLAN/PPPoE/port).">
              <input type="checkbox" checked={bulkMode} onChange={(e) => setBulkMode(e.target.checked)} data-testid="prov-bulk-toggle" className="w-4 h-4 accent-cyan-500" />
              Tryb masowy — wiele ONU naraz (opcjonalnie)
            </label>
            {bulkMode && (
              <>
                <textarea value={f.bulk} onChange={(e) => set("bulk", e.target.value)} data-testid="prov-bulk" rows={4}
                  placeholder={"Jedna ONU na linię: onu[,vlan[,ppp_user,ppp_pass]]\nnp.\n1,100,jan,haslo\n2\n3,101"} className={`${inp} font-mono text-xs mt-1`} />
                <label className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-cyan-400 hover:text-cyan-300 cursor-pointer" data-testid="prov-bulk-csv-label">
                  <Upload size={12} /> Import z pliku CSV (opcjonalnie)
                  <input type="file" accept=".csv,text/csv,text/plain" data-testid="prov-bulk-csv" onChange={handleCsv} className="hidden" />
                </label>
                <button type="button" onClick={downloadTemplate} data-testid="prov-bulk-template" className="mt-1 ml-3 inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-cyan-300"><Download size={12} /> Pobierz wzór CSV</button>
              </>
            )}
          </div>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-400 mt-3 cursor-pointer" title="Powiadomienie push trafia do montera z aktywnym zleceniem tego klienta">
          <input type="checkbox" checked={notify} onChange={(e) => { setNotify(e.target.checked); localStorage.setItem("fp_notify_onu", e.target.checked ? "1" : "0"); }} data-testid="prov-notify" className="w-4 h-4 accent-cyan-500" />
          Powiadom montera (push) po rejestracji ONU
        </label>
        <button onClick={run} disabled={busy} data-testid="prov-run" className="mt-2 w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded px-3 py-2 text-sm flex items-center justify-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <TerminalSquare size={16} />} {bulkMode ? "Wykonaj masowo" : "Wykonaj provisioning"}
        </button>
        {bulkRes && (
          <div className="mt-3 space-y-1.5" data-testid="prov-bulk-result">
            <div className="text-xs font-semibold text-cyan-300">Wynik masowy: {bulkRes.ok_count}/{bulkRes.count} OK</div>
            {bulkRes.results.map((r, i) => (
              <div key={i} className="text-[11px] flex items-center gap-2 bg-slate-900/50 rounded px-2 py-1" data-testid="prov-bulk-row">
                <span className={`font-semibold ${r.error ? "text-red-400" : r.ok ? "text-green-400" : "text-amber-300"}`}>{r.error ? "błąd" : r.ok ? "OK" : "?"}</span>
                <span className="text-slate-300">ONU {r.onu || "—"}</span>
                {r.vlan && <span className="text-slate-500">VLAN {r.vlan}</span>}
                {r.error && <span className="text-red-300">{r.error}</span>}
                {r.verify?.found && <span className="text-cyan-300 ml-auto">RX {r.verify.rx_dbm ?? "—"} dBm{r.saved_rx ? " ✓zapis" : ""}</span>}
              </div>
            ))}
          </div>
        )}
        {res && (
          <div className="mt-3" data-testid="prov-result">
            <div className={`text-xs font-semibold ${res.ok ? "text-green-400" : "text-red-400"}`}>{res.ok ? "OK" : "Błąd"}</div>
            {res.commands?.length > 0 && (
              <div className="mt-1">
                <div className="text-[10px] uppercase text-slate-500 mb-0.5">Wysłane komendy</div>
                <pre className="bg-black/50 rounded p-2 text-[11px] text-cyan-300 whitespace-pre-wrap max-h-40 overflow-y-auto">{res.commands.join("\n")}</pre>
              </div>
            )}
            <div className="text-[10px] uppercase text-slate-500 mb-0.5 mt-2">Transkrypt</div>
            <pre className="bg-black/50 rounded p-2 text-[11px] text-slate-300 whitespace-pre-wrap max-h-52 overflow-y-auto">{res.transcript}</pre>
            {res.verify && (
              <div className="mt-2 rounded border border-slate-700 bg-slate-900/60 p-2" data-testid="prov-verify">
                <div className="text-[10px] uppercase text-slate-500 mb-0.5">Weryfikacja ONU (SNMP)</div>
                {res.verify.found ? (
                  <div className="text-[11px] text-slate-300 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>Status: <b className={["online", "working", "up"].includes(String(res.verify.status).toLowerCase()) ? "text-green-400" : "text-amber-300"}>{res.verify.status || "—"}</b></span>
                    <span>RX: <b className="text-cyan-300">{res.verify.rx_dbm != null ? `${res.verify.rx_dbm} dBm` : "—"}</b></span>
                    <span>TX: <b>{res.verify.tx_dbm != null ? `${res.verify.tx_dbm} dBm` : "—"}</b></span>
                    {res.verify.sn && <span>S/N: {res.verify.sn}</span>}
                    {res.verify.port && <span>PON: {res.verify.port}</span>}
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-400">{res.verify.note || "Nie znaleziono ONU w odczycie."}</div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={liveOn ? stopLive : startLive} data-testid="prov-live-toggle" className="text-[11px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                    <RefreshCw size={11} className={liveOn ? "animate-spin" : ""} /> {liveOn ? "Zatrzymaj podgląd" : "Śledź status na żywo (30 s)"}
                  </button>
                </div>
                {live && (
                  <div className="mt-1 text-[11px]" data-testid="prov-live">
                    {live.found ? (
                      <span className="text-green-400">● ONU zarejestrowana — status {live.status || "online"}, RX {live.rx_dbm != null ? `${live.rx_dbm} dBm` : "—"}</span>
                    ) : (
                      <span className="text-slate-400">○ {live.note || "Oczekiwanie na rejestrację ONU…"}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">{res.hint}</div>
          </div>
        )}
        <div className="mt-3 border-t border-slate-800 pt-2">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={loadLogs} disabled={logsBusy} data-testid="prov-logs-load" className="text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-1.5">
              {logsBusy ? <Loader2 size={12} className="animate-spin" /> : <History size={12} />} Historia provisioningów{f.device_id ? " (to urządzenie)" : ""}
            </button>
            <button onClick={() => exportHistory("csv")} data-testid="prov-logs-export" className="text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-1.5"><Download size={12} /> Eksport CSV</button>
            <button onClick={() => exportHistory("pdf")} data-testid="prov-logs-export-pdf" className="text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-1.5"><Download size={12} /> Eksport PDF</button>
          </div>
          {logs && (
            <div className="mt-2 space-y-1.5 max-h-52 overflow-y-auto" data-testid="prov-logs-list">
              {logs.length === 0 && <div className="text-[11px] text-slate-500">Brak zapisanych provisioningów.</div>}
              {logs.map((l) => (
                <div key={l.id} className="rounded border border-slate-800 bg-slate-900/50 p-2 text-[11px]" data-testid="prov-log-row">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold ${l.ok ? "text-green-400" : "text-red-400"}`}>{l.ok ? "OK" : "błąd"}</span>
                    <span className="text-slate-300">{l.device_name}</span>
                    <span className="text-slate-500">· ONU {l.onu}/PON {l.port} · VLAN {l.vlan}</span>
                    <span className="text-slate-600 ml-auto">{(l.ts || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-slate-500 mt-0.5">{l.vendor} · {l.proto} · {l.user_name} · {l.commands?.length || 0} komend{l.verify?.found ? ` · RX ${l.verify.rx_dbm ?? "—"} dBm` : ""}</div>
                  <button onClick={() => applyLog(l)} data-testid="prov-log-repeat" className="mt-1 text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"><RotateCcw size={11} /> Ponów tę konfigurację</button>
                  {l.commands?.length > 0 && <pre className="mt-1 bg-black/40 rounded p-1.5 text-[10px] text-cyan-300/80 whitespace-pre-wrap max-h-24 overflow-y-auto">{l.commands.join("\n")}</pre>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
